import {
  useRef,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { useShapeAwareList } from './ShapeAwareListContext';

/**
 * =============================================================================
 * SHAPE-AWARE ITEM COMPONENT (SIMPLIFIED)
 * =============================================================================
 *
 * A wrapper component for list items that adjusts their width and margin
 * when they enter the dock cutout zone. This creates a "flow around dock"
 * effect without complex clip-path calculations.
 *
 * How it works:
 * 1. Measures its own position in container coordinates
 * 2. Checks if it overlaps with the dock zone (shelfY to floorY)
 * 3. If in zone: reduces width and adds margin on the dock side
 * 4. Left panel: gap on RIGHT (toward dock)
 * 5. Right panel: gap on LEFT (toward dock)
 *
 * =============================================================================
 */

interface ShapeAwareItemProps {
  /** Child content to render */
  children: ReactNode;
  /** Additional CSS class names */
  className?: string;
  /** Custom styles */
  style?: CSSProperties;
  /** As what element to render (default: div) */
  as?: 'div' | 'li' | 'article';
}

/**
 * ShapeAwareItem Component
 */
export function ShapeAwareItem({
  children,
  className = '',
  style,
  as: Component = 'div',
}: ShapeAwareItemProps): JSX.Element {
  const itemRef = useRef<HTMLDivElement>(null);
  const {
    panelSide,
    geometry,
    isDockHovered,
    scrollContainerRef,
  } = useShapeAwareList();

  // Track whether item is in dock zone
  const [inDockZone, setInDockZone] = useState(false);
  const [indent, setIndent] = useState(0);

  /**
   * Check position and calculate indent
   */
  const updatePosition = useCallback(() => {
    if (!itemRef.current || !geometry) {
      setInDockZone(false);
      setIndent(0);
      return;
    }

    // Get content-panels container for coordinate reference
    const contentPanelsElement = document.querySelector('.content-panels');
    if (!contentPanelsElement) {
      setInDockZone(false);
      setIndent(0);
      return;
    }

    const containerRect = contentPanelsElement.getBoundingClientRect();
    const itemRect = itemRef.current.getBoundingClientRect();

    // Convert to container coordinates
    const itemTop = itemRect.top - containerRect.top;
    const itemBottom = itemRect.bottom - containerRect.top;

    // Calculate inner geometry (matching ContentPanels.tsx)
    const innerWrapRadius = geometry.wrapRadius + (isDockHovered ? 6 : 10);
    const arcCenterLX = geometry.dockLeft + geometry.scaledCornerRadius;
    const arcCenterRX = geometry.dockRight - geometry.scaledCornerRadius;
    const arcCenterTopY = geometry.dockTop + geometry.scaledCornerRadius;
    const arcCenterBotY = geometry.dockBottom - geometry.scaledCornerRadius;

    // Inner shelf/floor (matching ContentPanels inner path)
    const innerShelfY = arcCenterTopY - innerWrapRadius;
    const innerFloorY = arcCenterBotY + innerWrapRadius;
    const innerLeftEdge = arcCenterLX - innerWrapRadius;
    const innerRightEdge = arcCenterRX + innerWrapRadius;

    // Check if item overlaps with dock zone
    const isInZone = itemBottom > innerShelfY && itemTop < innerFloorY;
    setInDockZone(isInZone);

    if (isInZone) {
      // Get the scroll container's position
      const scrollContainer = scrollContainerRef?.current;
      if (!scrollContainer) {
        setIndent(0);
        return;
      }

      const scrollRect = scrollContainer.getBoundingClientRect();

      if (panelSide === 'left') {
        // Left panel: simple indent on right side
        const panelRight = scrollRect.right - containerRect.left;
        const calculatedIndent = Math.max(0, panelRight - innerLeftEdge + 12);
        setIndent(calculatedIndent);
      } else {
        // Right panel: handled by container clip-path, minimal item adjustment
        const panelLeft = scrollRect.left - containerRect.left;
        const calculatedIndent = Math.max(0, innerRightEdge - panelLeft + 8);
        setIndent(calculatedIndent);
      }
    } else {
      setIndent(0);
    }
  }, [geometry, panelSide, isDockHovered, scrollContainerRef]);

  // Update on geometry/hover changes
  useEffect(() => {
    updatePosition();
  }, [updatePosition, geometry, isDockHovered]);

  // Update on scroll
  useEffect(() => {
    if (!scrollContainerRef?.current) {return;}

    const scrollContainer = scrollContainerRef.current;
    let rafId: number | null = null;

    const handleScroll = (): void => {
      if (rafId) {return;}
      rafId = requestAnimationFrame(() => {
        updatePosition();
        rafId = null;
      });
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [scrollContainerRef, updatePosition]);

  // Update on resize
  useEffect(() => {
    const handleResize = (): void => {
      updatePosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updatePosition]);

  // Compute dynamic styles
  const dynamicStyle: CSSProperties = {
    ...style,
    // Apply width and margin when in dock zone
    ...(inDockZone && indent > 0
      ? {
          width: `calc(100% - ${indent}px)`,
          marginLeft: panelSide === 'right' ? `${indent}px` : undefined,
        }
      : {}),
    transition: 'width 0.3s ease, margin-left 0.3s ease',
  };

  const itemClasses = [
    'shape-aware-item',
    inDockZone ? 'shape-aware-item--in-zone' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Type assertion needed for dynamic element
  const Element = Component as 'div';

  return (
    <Element
      ref={itemRef}
      className={itemClasses}
      style={dynamicStyle}
    >
      {children}
    </Element>
  );
}

export default ShapeAwareItem;
